/* eslint-disable no-unused-expressions */
import Promise from 'bluebird';
import _ from 'underscore';
import { QueryParam } from '@bingads-webui-react/scope-service';
import { withDFMCache, setDFMCache } from '@bingads-webui/dfm-cache';
import { get } from '@bingads-webui-universal/primitive-utilities';
import { isMultiAgencyBillingEnabled, isBillingLinkPermission } from '@bingads-webui/multi-agency-utils';
import { getAccountLinkPermissionType } from '../global-init/init-settings-context';

export function fetchData({
  serverContext,
  dfmPerfMarker,
  preferenceService,
  loadingPath,
  campaignType,
}) {
  return new Promise((resolve, reject) => {
    const queryParam = new QueryParam(window.location);
    // The below condition is to check if the agency user is limited by the agency billing link type.
    // If the user is limited, then the user is only allowed to see Budget & Billing statement reporting pages and other reporting related pages in CMUI.
    // In this case, we should not prefetch the data for unaccessible pages where 401 logout will be triggered.
    const isLimitedByBillingLink = isMultiAgencyBillingEnabled({ dynamic: serverContext.DynamicPermissions || {} })
      && !_.get(serverContext.Permissions, 'IsInternalUser')
      && isBillingLinkPermission({ AccountLinkPermissionType: getAccountLinkPermissionType(serverContext) });
    const isOdataGetProductScopeEnabled = _.get(serverContext, ['DynamicPermissions', 'OdataGetProductScope'], false);
    const isPAAIMConsolidationEnabled = _.get(serverContext, ['DynamicPermissions', 'PAAIMConsolidation'], false);

    if (!isLimitedByBillingLink && loadingPath === '/campaign/vnext/overview'
      && serverContext.Permissions.IsDataPreFetchForOverviewPageEnabledInVnext) {
      if (campaignType === 'PerformanceMax' && serverContext.DynamicPermissions.PMaxLite) {
        import(/* webpackMode: 'eager'  *//* webpackChunkName: "pmax-overview-page-dfm" */'./fetch-pmax-overview-page-data-webpack').then(({ fetchDataForPmaxOverview }) => {
          dfmPerfMarker.willInit();
          fetchDataForPmaxOverview({
            preferenceService,
            serverContext,
            resolve,
            reject,
          });
        });
      } else if (serverContext.DynamicPermissions.OverviewPageFluentV2
        || serverContext.DynamicPermissions.OverviewPageFluent2
        || serverContext.DynamicPermissions.AppAdsCampaignPhaseOne
        || serverContext.DynamicPermissions.UnifiedAppAds
        || (serverContext.DynamicPermissions.LinkedInCampaigns && campaignType === 'LinkedIn')
      ) {
        import(/* webpackMode: 'eager' *//* webpackChunkName: "fluent-overview-page-dfm" */'./fetch-fluent-overview-page-data-webpack').then(({ fetchDataForFluentOverview }) => {
          dfmPerfMarker.willInit();
          fetchDataForFluentOverview({
            serverContext,
            preferenceService,
            resolve,
            reject,
            queryParam,
          });
        });
      } else {
        import(/* webpackMode: 'eager' *//* webpackChunkName: "overview-page-dfm" */'./fetch-overview-page-data-webpack').then(({ fetchDataForOverview }) => {
          dfmPerfMarker.willInit();
          fetchDataForOverview(serverContext, preferenceService, resolve, reject);
        });
      }
    } else if (!isLimitedByBillingLink && loadingPath === '/campaign/vnext/campaigns'
      && serverContext.Permissions.IsDataPreFetchForCampaignsPageEnabledInVnext) {
      import(/* webpackMode: 'eager' *//* webpackChunkName: "campaigns-page-dfm" */'./fetch-campaigns-page-data-webpack').then(({ fetchDataForCampaigns }) => {
        dfmPerfMarker.willInit();
        const campaignDfm = fetchDataForCampaigns({
          serverContext,
          resolve,
          preferenceService,
          // Seems this i18n isn't used anyways
          newI18n: { getString: () => '' },
        });

        const { dataSource, p$dfm } = campaignDfm;
        dataSource.query = withDFMCache(dataSource.query, 'campaignDFM', dataSource);
        setDFMCache('campaignDFM', p$dfm);
      });
    } else if (!isLimitedByBillingLink && (loadingPath === '/campaign/vnext/accounts/overview' ||
      loadingPath === '/campaign/vnext/accounts/overview/smart' ||
      loadingPath === '/campaign/vnext/accounts/overview/expert') && serverContext.DynamicPermissions.CustomerLevelOverviewFluent) {
      import(/* webpackMode: 'eager' *//* webpackChunkName: "customer-overview-page-dfm" */'./fetch-customer-overview-page-data-webpack').then(({ fetchDataForAccountsOverview }) => {
        dfmPerfMarker.willInit();
        fetchDataForAccountsOverview({ serverContext, resolve, reject });
      });
    } else if (!isLimitedByBillingLink && (loadingPath === '/campaign/vnext/accounts/overview' ||
      loadingPath === '/campaign/vnext/accounts/overview/smart' ||
      loadingPath === '/campaign/vnext/accounts/overview/expert')) {
      import(/* webpackMode: 'eager' *//* webpackChunkName: "overview-page-dfm" */'./fetch-accounts-overview-page-data-webpack').then(({ fetchDataForAccountsOverview }) => {
        dfmPerfMarker.willInit();
        fetchDataForAccountsOverview(serverContext, resolve, preferenceService, loadingPath);
      });
    } else if (!isLimitedByBillingLink && loadingPath === '/campaign/vnext/accounts/performance' &&
      serverContext.Permissions.IsDataPreFetchForPerformancePageEnabledInVnext) {
      import(/* webpackMode: 'eager' *//* webpackChunkName: "performance-page-dfm" */'./fetch-performance-page-data-webpack').then(({ fetchDataForPerformance }) => {
        dfmPerfMarker.willInit();
        fetchDataForPerformance(serverContext, resolve, preferenceService, loadingPath);
      });
    } else if (!isLimitedByBillingLink && loadingPath === '/campaign/vnext/createcampaign/objective' &&
      serverContext.Permissions.IsDataPreFetchForPerformancePageEnabledInVnext) {
      if (isOdataGetProductScopeEnabled) {
        import(/* webpackChunkName: "campaign-creation-objective-page-dfm" */ './fetch-search-shopping-product-scope-data')
          .then(({ fetchSearchShoppingProductScopeData }) => {
            const p$dfm = fetchSearchShoppingProductScopeData(resolve, serverContext);
            setDFMCache('getProductScopeDFM', p$dfm);
            setDFMCache('getPmaxProductScopeDFM', p$dfm);
          });
        import(/* webpackChunkName: "campaign-creation-objective-page-dfm" */ './fetch-audience-shopping-product-scope-data')
          .then(({ fetchAudienceShoppingProductScopeData }) => {
            const ap$dfm = fetchAudienceShoppingProductScopeData(resolve, serverContext);
            setDFMCache('getAudienceProductScopeDFM', ap$dfm);
          });
      } else {
        import(/* webpackChunkName: "campaign-creation-objective-page-dfm" */'./fetch-shopping-campaign-creation-page-data')
          .then(({ fetchDataForShoppingCampaignCreationPage }) => {
            const isMranSimplificationV2Enabled = serverContext.DynamicPermissions.MranSimplificationV2;
            const p$dfm = fetchDataForShoppingCampaignCreationPage(resolve, isMranSimplificationV2Enabled);
            setDFMCache('getProductScopeDFM', p$dfm);
          });
        import(/* webpackChunkName: "campaign-creation-objective-page-dfm" */'./fetch-audience-shopping-campaing-creation-page-data')
          .then(({ fetchProductDataForAudienceShoppingCampaignCreationPage }) => {
            const ap$dfm = fetchProductDataForAudienceShoppingCampaignCreationPage(serverContext);
            setDFMCache('getAudienceProductScopeDFM', ap$dfm);
            resolve();
          });
      }
      import(/* webpackChunkName: "campaign-creation-objective-page-dfm" */'./fetch-has-conversion-goal-data')
        .then(({ fetchHasConversionGoalData }) => {
          const p$dfm = fetchHasConversionGoalData();
          setDFMCache('getHasConversionGoalDFM', p$dfm);
          resolve();
        });
    } else if (!isLimitedByBillingLink && (loadingPath === '/campaign/vnext/create-campaign/msstoreads') &&
      serverContext.Permissions.IsDataPreFetchForPerformancePageEnabledInVnext) {
      if (isOdataGetProductScopeEnabled) {
        import(/* webpackChunkName: "shopping-campaign-creation-page-dfm" */ './fetch-search-shopping-product-scope-data')
          .then(({ fetchSearchShoppingProductScopeData }) => {
            const p$dfm = fetchSearchShoppingProductScopeData(resolve, serverContext);
            setDFMCache('getProductScopeDFM', p$dfm);
          });
      } else {
        import(/* webpackChunkName: "shopping-campaign-creation-page-dfm" */'./fetch-shopping-campaign-creation-page-data')
          .then(({ fetchDataForShoppingCampaignCreationPage }) => {
            const p$dfm = fetchDataForShoppingCampaignCreationPage(resolve);
            setDFMCache('getProductScopeDFM', p$dfm);
          });
      }
    } else if (!isLimitedByBillingLink && loadingPath === '/campaign/vnext/createcampaign/audienceshopping' && !isPAAIMConsolidationEnabled) {
      if (isOdataGetProductScopeEnabled) {
        import(/* webpackChunkName: "audience-shopping-campaign-creation-page-dfm" */ './fetch-audience-shopping-product-scope-data')
          .then(({ fetchAudienceShoppingProductScopeData }) => {
            const ap$dfm = fetchAudienceShoppingProductScopeData(resolve, serverContext);
            setDFMCache('getAudienceProductScopeDFM', ap$dfm);
          });
      } else {
        import(/* webpackChunkName: "audience-shopping-campaign-creation-page-dfm" */'./fetch-audience-shopping-campaing-creation-page-data')
          .then(({ fetchProductDataForAudienceShoppingCampaignCreationPage }) => {
            const ap$dfm = fetchProductDataForAudienceShoppingCampaignCreationPage(serverContext);
            setDFMCache('getAudienceProductScopeDFM', ap$dfm);
            resolve();
          });
      }
      import(/* webpackChunkName: "audience-shopping-campaign-creation-page-dfm" */'./fetch-audience-shopping-campaing-creation-page-data')
        .then(({ fetchFeedDataForAudienceShoppingCampaignCreationPage }) => {
          let f$dfm;
          if (serverContext.Permissions.IsEnabledForDynamicFeedCampaign) {
            f$dfm = fetchFeedDataForAudienceShoppingCampaignCreationPage(serverContext);
            setDFMCache('getFeedScopeDFM', f$dfm);
          }
          resolve();
        });
    } else if (!isLimitedByBillingLink && (loadingPath.includes('vnext/recommendations')
      || (loadingPath.includes('vnext/accounts/recommendations') && queryParam.values.recommendationType === undefined))) {
      import(/* webpackChunkName: "recommendation-page-dfm" */'./fetch-recommendation-page-data')
        .then(({ fetchSummaryDataForRecommendationPage }) => {
          const previousRoute = history.state.state
            && history.state.state.previousRoute;
          if (!previousRoute
            || (previousRoute && !previousRoute.includes('/recommendations/'))) {
            const p$dfm = fetchSummaryDataForRecommendationPage(serverContext, preferenceService, queryParam, loadingPath);
            setDFMCache('getSummaryDFM', p$dfm);
          }
          resolve();
        });
    } else if (!isLimitedByBillingLink && loadingPath === '/campaign/vnext/ads' && serverContext.DynamicPermissions.AdsPageDFMV2) {
      import(/* webpackMode: 'eager' *//* webpackChunkName: "ads-page-dfm" */'./fetch-ads-page-data')
        .then(({ fetchDataForAds }) => {
          dfmPerfMarker.willInit();
          const adsDfm = fetchDataForAds({
            resolve,
            queryParam,
            serverContext,
            preferenceService,
          });
          const { dataSource, p$dfm } = adsDfm;
          dataSource.query = withDFMCache(dataSource.query, 'adsDFM', dataSource);
          setDFMCache('adsDFM', p$dfm);
        });
    } else if (!isLimitedByBillingLink && loadingPath === '/campaign/vnext/keywords') {
      import(/* webpackMode: 'eager' *//* webpackChunkName: "keywords-page-dfm" */'./fetch-positive-keywords-page-data')
        .then(({ fetchDataForKeywords }) => {
          dfmPerfMarker.willInit();
          const keywordsDfm = fetchDataForKeywords({
            resolve,
            queryParam,
            serverContext,
            preferenceService,
          });
          const { dataSource, p$dfm } = keywordsDfm;
          dataSource.query = withDFMCache(dataSource.query, 'keywordsDFM', dataSource);
          setDFMCache('keywordsDFM', p$dfm);
        });
    } else if (!isLimitedByBillingLink && loadingPath === '/campaign/vnext/adgroups' && serverContext.DynamicPermissions.AdGroupsPageDFMV2) {
      import(/* webpackMode: 'eager' *//* webpackChunkName: "adgroups-page-dfm" */'./fetch-adgroups-page-data')
        .then(({ fetchDataForAdGroups }) => {
          dfmPerfMarker.willInit();
          const adGroupsDfm = fetchDataForAdGroups({
            resolve,
            queryParam,
            serverContext,
            preferenceService,
          });
          const { dataSource, p$dfm } = adGroupsDfm;
          dataSource.query = withDFMCache(dataSource.query, 'adGroupsDFM', dataSource);
          setDFMCache('adGroupsDFM', p$dfm);
        });
    } else if (!isLimitedByBillingLink && loadingPath.includes('/campaign/vnext/extensions') && loadingPath !== '/campaign/vnext/extensions/automated' && serverContext.DynamicPermissions.AdExtensionsPageDFMV2) {
      import(/* webpackChunkName: "adextensions-page-dfm" */'./fetch-adextensions-page-data')
        .then(({ fetchDataForAdExtensions }) => {
          dfmPerfMarker.willInit();
          const adExtensionsDfm = fetchDataForAdExtensions({
            loadingPath,
            resolve,
            queryParam,
            serverContext,
            preferenceService,
          });
          const { dataSource, p$dfm } = adExtensionsDfm;
          dataSource.query = withDFMCache(dataSource.query, 'adExtensionsDFM', dataSource);
          setDFMCache('adExtensionsDFM', p$dfm);
        });
    } else if (!isLimitedByBillingLink && loadingPath === '/campaign/vnext/budgetcenter') {
      import(/* webpackMode: 'eager' *//* webpackChunkName: "budget-center-page-dfm" */'./fetch-budget-center-page-data')
        .then(({ fetchDataForBudgetCenter }) => {
          dfmPerfMarker.willInit();
          fetchDataForBudgetCenter({
            serverContext,
            queryParam,
          });
          resolve();
        });
    } else if (loadingPath === '/campaign/vnext/reports-v4/default-reports') {
      import(/* webpackChunkName: "reports-v4-page-dfm" */'./fetch-reports-v4-page-data')
        .then(({ fetchDataForDefaultReportsPage }) => {
          const p$dfm = fetchDataForDefaultReportsPage(serverContext, queryParam);
          setDFMCache('getDefaultReportsDFM', p$dfm);
          resolve();
        });
    } else if (loadingPath === '/campaign/vnext/reports-v4/my-reports/customized') {
      import(/* webpackChunkName: "reports-v4-page-dfm" */'./fetch-reports-v4-page-data')
        .then(({ fetchDataForMyReportsPage }) => {
          const type = 'Saved';
          const p$dfm = fetchDataForMyReportsPage(serverContext, queryParam, type);
          setDFMCache(`get${type}ReportsDFM`, p$dfm);
          resolve();
        });
    } else if (loadingPath === '/campaign/vnext/reports-v4/my-reports/shared') {
      import(/* webpackChunkName: "reports-v4-page-dfm" */'./fetch-reports-v4-page-data')
        .then(({ fetchDataForMyReportsPage }) => {
          const type = 'Shared';
          const p$dfm = fetchDataForMyReportsPage(serverContext, queryParam, type);
          setDFMCache(`get${type}ReportsDFM`, p$dfm);
          resolve();
        });
    } else if (loadingPath.includes('/campaign/vnext/reports-v4/create')) {
      import(/* webpackChunkName: "reports-v4-page-dfm" */'./fetch-reports-v4-page-data')
        .then(({ fetchDataForRecentReportsPage }) => {
          const [, reportAction, reportType] = location.pathname.match(/\/(edit|create)\/(.*)/);
          if (!(reportType === 'matchingreport' || reportType === 'pricefeedstatusreport')) { // these reports from hotel center, recent reports not supported
            const p$dfm = fetchDataForRecentReportsPage(serverContext, queryParam);
            setDFMCache(`getRecentReports${reportAction}${reportType}DFM`, p$dfm);
          }
          resolve();
        });
    } else if (loadingPath.includes('/campaign/vnext/reports-v4/edit')) {
      let fetchTaskItemCall;
      let fetchDataForRecentReportsPageCall;
      let fetchInsertionOrdersCall;
      const isInternalUser = serverContext.CurrentUser.IsInternal;
      const isInsertionOrderReportEnabled = serverContext.DynamicPermissions.InsertionOrderReport;
      import(/* webpackChunkName: "reports-v4-page-dfm" */'./fetch-reports-v4-page-data')
        .then(({ fetchTaskItem, fetchDataForRecentReportsPage, fetchInsertionOrders }) => {
          fetchTaskItemCall = fetchTaskItem;
          fetchDataForRecentReportsPageCall = fetchDataForRecentReportsPage;
          fetchInsertionOrdersCall = fetchInsertionOrders;
          const taskItem = isInternalUser ?
            fetchTaskItemCall(serverContext, queryParam, true) :
            fetchTaskItemCall(serverContext, queryParam)
              .catch((err) => { // eslint-disable-line consistent-return
                if (err.status === 400) {
                  const body = err.responseJSON;
                  if (get(body, 'value[0].Code') === 'InvalidId') {
                    // retry with user's parent cid
                    return fetchTaskItemCall(serverContext, queryParam, true);
                  }
                }
              });
          return taskItem;
        })
        .catch(() => {
          // reject the promise
          reject();
        })
        .then((taskItem) => {
          if (taskItem) {
            const [, reportAction, reportId] = location.pathname.match(/\/(edit|create)\/(.*)/);
            const p$dfm = fetchDataForRecentReportsPageCall(serverContext, queryParam, taskItem);
            setDFMCache(`getRecentReports${reportAction}${reportId}DFM`, p$dfm);
            if (isInsertionOrderReportEnabled && taskItem.ReportType === 'InsertionOrderReport') {
              fetchInsertionOrdersCall(serverContext, queryParam);
            }
          }
          resolve();
        });
    } else {
      resolve();
    }
  });
}
